'use client';

import { Grid2, type Theme, Typography, useTheme } from '@mui/material';
import { Trans } from 'react-i18next';
import { Text } from './Text';
import { useLanguageStore } from '@/infrastructure/zustand/languageStoreProvider';
import { CountryNames } from '@/infrastructure/locales/countryNames';
import type { Campaign, CampaignText } from '@/infrastructure/zustand/types/campaign.interface';
import type { Language } from '@/shared';

export const useBannerSx = (theme: Theme) => ({
	campaignBox: {
		paddingTop: '20px',
		[theme.breakpoints.up('sm')]: {
			padding: '20px 0',
		},
	},
	campaignName: {
		color: theme.palette.secondary.main,
		fontSize: '24px',
		lineHeight: '24px',
		[theme.breakpoints.up('sm')]: {
			padding: '0 7%',
		},
		[theme.breakpoints.between('sm', 'sm')]: {
			textAlign: 'center',
		},
	},
	countryName: {
		fontSize: '14px',
		marginTop: '20px',
		[theme.breakpoints.up('md')]: {
			paddingLeft: '7%',
		},
	},
	campaignBy: {
		'backgroundColor': '#eaeaea',
		'& p': {
			padding: '8px',
			fontSize: '12px',
			color: '#fff',
			display: 'none',
			[theme.breakpoints.up('sm')]: {
				display: 'block',
				color: theme.palette.text.primary,
			},
		},
		[theme.breakpoints.up('sm')]: {
			backgroundColor: '#fff',
		},
	},
});

const defaultCampaignBoxBackgroundColor = '#eaeaea';

function getCountryName(countryCode: string, langCode: Language): string {
	const countries =
		langCode in CountryNames ? CountryNames[langCode as keyof typeof CountryNames] : CountryNames['ENG'];

	return countryCode in CountryNames ? countries[countryCode as keyof typeof countries] : '';
}

export const Banner = ({
	position = 'static',
	customCountries,
	customCampaignByText,
	campaignTexts,
	campaign,
}: {
	position: 'static' | 'relative';
	customCountries: string | undefined;
	customCampaignByText: string | undefined;
	campaignTexts: CampaignText;
	campaign: Campaign;
}) => {
	const currentLangCode = useLanguageStore((state) => state.currentLangCode);
	const theme = useTheme<Theme>();
	const sx = useBannerSx(theme);

	const countries =
		customCountries ??
		[getCountryName(campaign.countryCode ?? '', currentLangCode), campaign.cityName]
			.filter((x) => x !== undefined)
			.join('-')
			.toUpperCase();

	return (
		<Grid2
			container
			direction="row"
			style={{ position: position, borderTop: '1px solid #ddd', borderBottom: '1px solid #ddd' }}
		>
			<Grid2
				size={{ xs: 12, sm: 9 }}
				sx={sx.campaignBox}
				style={{ backgroundColor: defaultCampaignBoxBackgroundColor }}
			>
				<Text
					disableColorDetection
					text={campaignTexts?.name}
					sx={sx.campaignName}
					style={{ color: theme.palette.primary.main }}
				/>
				<Text disableColorDetection text={countries} sx={sx.countryName} />
			</Grid2>
			<Grid2 size={{ xs: 12, sm: 3 }} sx={sx.campaignBy}>
				<Grid2
					container
					direction="column"
					justifyContent="center"
					alignItems="center"
					style={{
						flexDirection: 'column',
					}}
				>
					<Typography sx={sx.campaignBy}>{customCampaignByText ?? <Trans i18nKey="aCampaignBy" />} </Typography>

					{campaign.ngos.length > 1 ? (
						<section className="slide-section">
							{/* We put images twice to make the infinite slide effect */}
							<div className="slide-animate">
								{[...campaign.ngos, ...campaign.ngos].map((ngo) => (
									// eslint-disable-next-line @next/next/no-img-element
									<img
										key={ngo.id}
										src={ngo.logo}
										alt={ngo.name}
										style={{ height: '100%', width: 'auto', padding: '5px', maxWidth: '200px', objectFit: 'contain' }}
									/>
								))}
							</div>
						</section>
					) : (
						<div style={{ display: 'flex', alignItems: 'center', height: 100 }}>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img
								src={campaign.ngos[0]?.logo}
								alt="logo"
								style={{
									height: '100%',
									width: 'auto',
									padding: '5px',
									maxWidth: '100%',
									objectFit: 'contain',
								}}
							/>
						</div>
					)}
				</Grid2>
			</Grid2>
		</Grid2>
	);
};
